<template>
    <div class="max-w-screen-md space-y-4 py-2 md:py-4">
        <div v-if="dues !== null">
            <span class="font-semibold">{{ duesLabel }}</span
            >&nbsp;&nbsp;<span class="text-lg">{{ dues }}</span>
            <br />
            <div class="text-xs italic">
                *Sales tax will be calculated at the time of billing
            </div>
        </div>

        <div v-if="formattedNextBillDate !== null">
            <span class="font-semibold">Next Bill Date:</span>&nbsp;&nbsp;<span
                class="text-lg"
                >{{ formattedNextBillDate }}</span
            >
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const DuesPricingDisplayDuesPricingPreviewItemFragment =
    graphql(/** GraphQL */ `
        fragment DuesPricingDisplayDuesPricingPreviewItemFragment on DuesPricingPreviewItem {
            quantity
            price
        }
    `)
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { Decimal } from '~/composables/support/decimal'
import type {
    BillingInterval,
    DuesPricingDisplayDuesPricingPreviewItemFragmentFragment
} from '~/resources/graphql/graphql'
import {
    formatTimelessDate,
    parseTimeless
} from '~/composables/support/localization'

const props = defineProps<{
    duesPricing:
        | DuesPricingDisplayDuesPricingPreviewItemFragmentFragment[]
        | null
        | undefined
    billingInterval: BillingInterval
    nextBillDate: string | null | undefined
}>()

const dues = computed(() => {
    if (props.duesPricing === undefined || props.duesPricing === null) {
        return null
    }

    let total = Decimal.zero()
    props.duesPricing.forEach(item => {
        if (item.price !== null) {
            const quantity = new Decimal(item.quantity.toString())
            total = total.plus(new Decimal(item.price).multipliedBy(quantity))
        }
    })
    return total.formatCurrency()
})

const duesLabel = computed(() => {
    if (isWeekly.value) {
        return 'Weekly Dues:'
    } else if (isFortnightly.value) {
        return 'Fortnightly Dues:'
    } else if (isMonthly.value) {
        return 'Monthly Dues:'
    } else if (isQuarterly.value) {
        return 'Quarterly Dues:'
    } else if (isYearly.value) {
        return 'Yearly Dues:'
    } else if (isDaily.value) {
        return 'Daily Dues:'
    } else {
        throw new Error()
    }
})

const isWeekly = computed(() => props.billingInterval === 'WEEKLY')
const isFortnightly = computed(() => props.billingInterval === 'FORTNIGHTLY')
const isMonthly = computed(() => props.billingInterval === 'MONTHLY')
const isQuarterly = computed(() => props.billingInterval === 'QUARTERLY')
const isYearly = computed(() => props.billingInterval === 'YEARLY')
const isDaily = computed(() => props.billingInterval === 'DAILY')

const formattedNextBillDate = computed(() => {
    if (props.nextBillDate === null || props.nextBillDate === undefined) {
        return null
    }
    return formatTimelessDate(parseTimeless(props.nextBillDate))
})
</script>
